<template>
  <div class="report-content-modal" v-if="showModal" @click.self="closeModal">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="text-xl font-bold text-white mb-2">Report Content</h3>
        <button @click="closeModal" class="close-button">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <p class="text-gray-300 mb-4">
          Help us maintain a safe and appropriate gaming environment. Please select the reason for reporting this content:
        </p>

        <form @submit.prevent="submitReport">
          <!-- 报告类型 -->
          <div class="form-group mb-4">
            <label class="block text-white font-medium mb-2">Report Reason</label>
            <div class="space-y-2">
              <label class="flex items-center">
                <input type="radio" v-model="reportData.reason" value="inappropriate" class="mr-3">
                <span class="text-gray-300">Inappropriate or offensive content</span>
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="reportData.reason" value="spam" class="mr-3">
                <span class="text-gray-300">Spam or unwanted content</span>
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="reportData.reason" value="copyright" class="mr-3">
                <span class="text-gray-300">Copyright infringement</span>
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="reportData.reason" value="malware" class="mr-3">
                <span class="text-gray-300">Malware or security threat</span>
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="reportData.reason" value="violence" class="mr-3">
                <span class="text-gray-300">Violence or harmful content</span>
              </label>
              <label class="flex items-center">
                <input type="radio" v-model="reportData.reason" value="other" class="mr-3">
                <span class="text-gray-300">Other</span>
              </label>
            </div>
          </div>

          <!-- 详细描述 -->
          <div class="form-group mb-4">
            <label for="description" class="block text-white font-medium mb-2">
              Description (Optional)
            </label>
            <textarea
              id="description"
              v-model="reportData.description"
              rows="4"
              class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="Please provide additional details about the issue..."
            ></textarea>
          </div>

          <!-- 联系信息 -->
          <div class="form-group mb-6">
            <label for="email" class="block text-white font-medium mb-2">
              Email (Optional)
            </label>
            <input
              type="email"
              id="email"
              v-model="reportData.email"
              class="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
              placeholder="your.email@example.com"
            />
            <p class="text-sm text-gray-400 mt-1">
              We may contact you if we need more information about your report.
            </p>
          </div>

          <!-- 提交按钮 -->
          <div class="flex justify-end space-x-3">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 text-gray-300 hover:text-white border border-gray-600 hover:border-gray-500 rounded-lg transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!reportData.reason || isSubmitting"
              class="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
            >
              {{ isSubmitting ? 'Submitting...' : 'Submit Report' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- 报告成功提示 -->
  <div v-if="showSuccessMessage" class="success-toast">
    <div class="toast-content">
      <svg class="w-6 h-6 text-green-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
      <div>
        <p class="font-medium text-white">Report Submitted</p>
        <p class="text-sm text-gray-300">Thank you for helping us maintain a safe community.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'

export default {
  name: 'ReportContent',
  props: {
    contentType: {
      type: String,
      default: 'general' // game, comment, user, general
    },
    contentId: {
      type: String,
      default: ''
    },
    contentUrl: {
      type: String,
      default: ''
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const showModal = ref(false)
    const showSuccessMessage = ref(false)
    const isSubmitting = ref(false)

    const reportData = reactive({
      reason: '',
      description: '',
      email: '',
      contentType: props.contentType,
      contentId: props.contentId,
      contentUrl: props.contentUrl || window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent
    })

    const openModal = () => {
      showModal.value = true
      // 更新内容信息
      reportData.contentUrl = window.location.href
      reportData.timestamp = new Date().toISOString()
    }

    const closeModal = () => {
      showModal.value = false
      // 重置表单
      reportData.reason = ''
      reportData.description = ''
      reportData.email = ''
      emit('close')
    }

    const submitReport = async () => {
      if (!reportData.reason) return

      isSubmitting.value = true

      try {
        // 模拟API调用 - 在实际应用中，这里应该调用后端API
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 发送报告数据到后端
        const reportPayload = {
          ...reportData,
          reportId: `report_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }

        // 在实际应用中，这里应该是API调用
        console.log('Content report submitted:', reportPayload)

        // 可以发送邮件通知管理员
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'content_report', {
            event_category: 'Safety',
            event_label: reportData.reason,
            content_type: reportData.contentType
          })
        }

        // 显示成功消息
        showSuccessMessage.value = true
        closeModal()

        // 3秒后隐藏成功消息
        setTimeout(() => {
          showSuccessMessage.value = false
        }, 3000)

      } catch (error) {
        console.error('Error submitting report:', error)
        alert('Failed to submit report. Please try again or contact us directly at zhangshuaidoc@163.com')
      } finally {
        isSubmitting.value = false
      }
    }

    // 暴露方法给父组件
    const show = () => openModal()

    return {
      showModal,
      showSuccessMessage,
      isSubmitting,
      reportData,
      openModal,
      closeModal,
      submitReport,
      show
    }
  }
}
</script>

<style scoped>
.report-content-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background-color: #1f2937;
  border-radius: 12px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #374151;
  margin-bottom: 24px;
}

.close-button {
  color: #9ca3af;
  transition: color 0.2s ease-in-out;
  padding: 4px;
}

.close-button:hover {
  color: #ffffff;
}

.modal-body {
  padding: 0 24px 24px 24px;
}

.form-group input[type="radio"] {
  accent-color: #3b82f6;
}

.success-toast {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1100;
  animation: slideInRight 0.3s ease-out;
}

.toast-content {
  background-color: #065f46;
  border: 1px solid #047857;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 10px 25px -3px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 640px) {
  .report-content-modal {
    padding: 10px;
  }
  
  .modal-content {
    max-height: 95vh;
  }
  
  .modal-header,
  .modal-body {
    padding-left: 16px;
    padding-right: 16px;
  }
  
  .success-toast {
    top: 10px;
    right: 10px;
    left: 10px;
  }
  
  .toast-content {
    width: 100%;
  }
}

/* 防止背景滚动 */
.report-content-modal {
  overflow: hidden;
}

/* 高对比度模式支持 */
@media (prefers-contrast: high) {
  .modal-content {
    border: 2px solid #ffffff;
  }
  
  .form-group input,
  .form-group textarea {
    border: 2px solid #ffffff;
  }
}

/* 减少动画的用户偏好 */
@media (prefers-reduced-motion: reduce) {
  .success-toast {
    animation: none;
  }
  
  .close-button,
  button {
    transition: none;
  }
}
</style>
